import {
  Component,
  OnDestroy,
  ElementRef,
  Input,
  HostBinding,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  GetPerformanceOverviewParams,
  ReportingService,
} from '../../../services/reporting/reporting.service';
import { sub } from 'date-fns';
import {
  ApiRequestService,
  NetSocialAccountFields,
} from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { from, Subscription } from 'rxjs';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import { UserService } from 'netsocial-posting/src/app/services/user/user.service';
import { observable, computed } from 'mobx-angular';
import { formatForApi } from 'netsocial-lib/src/app/utils/date-utils';
import { propertyDidChange } from 'netsocial-lib/src/app/utils/component-utils';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { PerformanceStatistics } from '../../../interfaces/performance-statistics';

interface Statistic {
  name: string;
  previous: string;
  change: string;
  indication: string;
  current: string;
}

interface StatisticGroup {
  name: string;
  stats: Statistic[];
}

@Component({
  selector: 'performance-grid',
  templateUrl: './performance-grid.component.html',
  styleUrls: ['./performance-grid.component.scss'],
})
export class PerformanceGridComponent implements OnChanges, OnDestroy {
  @observable @Input() period? = 7;
  @observable @Input() startDate: Date;
  @observable @Input() endDate: Date;
  @observable
  @Input()
  accountService: GetPerformanceOverviewParams['accountService'];

  @observable @Input() startDateCompare?: Date;
  @observable @Input() endDateCompare?: Date;

  @observable @Input() showHeader = true;
  @observable @Input() showPeriod = true;

  @observable @Input() view = 'personal';

  @HostBinding('class.compact')
  @Input()
  compact: boolean;

  @Input() analyze: boolean;

  periodOptions = [7, 30, 60, 90];

  readonly allowedPerformanceStats: Array<keyof PerformanceStatistics> = [
    'deliveries',
    'clicks',
    'shares',
    'impressions',
    'value',
    'likes',
    'accounts',
  ];

  @observable performanceStatistics?: PerformanceStatistics;
  @observable compareStatistics?: PerformanceStatistics;
  @observable viewOptions = ['personal', 'company', 'organization'];

  @computed
  get isDesktop(): boolean {
    return this.responsiveService.isDesktop;
  }

  @computed
  get data(): StatisticGroup[] | undefined {
    const data: StatisticGroup[] = [];
    this.allowedPerformanceStats.forEach((stat) => {
      const group: StatisticGroup = {
        name: stat,
        stats: [],
      };

      const stats = [this.performanceStatistics];

      if (this.compareStatistics) {
        stats.push(this.compareStatistics);
      }

      stats.forEach((_stats, index) => {
        if (_stats && _stats[stat]) {
          const { previous, change, indication, current } = _stats[stat];
          group.stats.push({
            name: index ? 'previousText' : 'currentText',
            previous,
            change: parseInt(change).toFixed(0) + '%',
            indication,
            current:
              current[0] === '$'
                ? '$' + Number(current.substring(1, current.length)).toFixed(2)
                : current,
          });
        }
      });

      data.push(group);
    });

    return data.length ? data : undefined;
  }

  @observable contextOwner?: NetSocialAccountFields;

  @computed
  get loading(): boolean {
    return !!this.getPerformanceDataSubscription || !!this.getPerformanceDataComparisonSubscription;
  }

  private contextOwnerSubscription: Subscription;
  @observable private getPerformanceDataSubscription?: Subscription;
  @observable private getPerformanceDataComparisonSubscription?: Subscription;

  constructor(
    readonly el: ElementRef<HTMLElement>,
    readonly userService: UserService,
    private readonly reportingService: ReportingService,
    private readonly apiRequest: ApiRequestService,
    private readonly errorService: ErrorService,
    private readonly responsiveService: ResponsiveService
  ) {
    this.contextOwnerSubscription = this.apiRequest.getContextOwner().subscribe((value) => {
      this.contextOwner = value;
      this.getPerformanceData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (propertyDidChange(changes).length) {
      this.getPerformanceData();
    }
  }

  ngOnDestroy(): void {
    this.contextOwnerSubscription?.unsubscribe();
    this.getPerformanceDataSubscription?.unsubscribe();
    this.getPerformanceDataComparisonSubscription?.unsubscribe();
  }

  async getPerformanceData(period?: GetPerformanceOverviewParams['period']): Promise<void> {
    if (period) {
      this.period = period;
    }
    const params: GetPerformanceOverviewParams = {};
    if (this.accountService) {
      params.accountService = this.accountService;
    }
    if (this.period) {
      const periodEndDate = new Date();
      this.endDate = periodEndDate;
      this.startDate = sub(this.endDate, { days: this.period });
    }
    if (this.startDate && this.endDate) {
      params.startDate = formatForApi(this.startDate);
      params.endDate = formatForApi(this.endDate);
    }
    if (this.view !== undefined) {
      params.view = this.view;
    }

    if (this.userService.currentUser?.clientInfo.isAgency === '1') {
      params.clientFilter = this.contextOwner?.ownerId;
    }

    this.getPerformanceDataSubscription = from(
      this.errorService.resolve(this.reportingService.getPerformanceOverview(params), {
        errorMessage: 'components.performance.errors.getPerformance',
      })
    ).subscribe((performanceStatistics) => {
      this.performanceStatistics = performanceStatistics;
      this.getPerformanceDataSubscription = undefined;
    });

    if (this.startDateCompare && this.endDateCompare) {
      const compareParams = {
        startDate: formatForApi(this.startDateCompare),
        endDate: formatForApi(this.endDateCompare),
      };

      this.getPerformanceDataComparisonSubscription = from(
        this.errorService.resolve(this.reportingService.getPerformanceOverview(compareParams), {
          errorMessage: 'components.performance.errors.getPerformance',
        })
      ).subscribe((compareStatistics) => {
        this.compareStatistics = compareStatistics;
        this.getPerformanceDataComparisonSubscription = undefined;
      });
    } else {
      this.compareStatistics = undefined;
    }
  }
}
